import { Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { WaterfallPageReorderList } from '@/modules/presentation/components/WaterfallPageReorderList';
import { PathsOf } from '@/types/subform';

import { usePresentationTemplateEditorContext } from '../../../PresentationTemplateEditor.context';
import {
  PresentationTemplateEditorBundle,
  PresentationTemplateEditorFormShape,
} from '../../../PresentationTemplateEditor.types';
import {
  buildWaterfallBundleFromForm,
  getDefaultWaterfallPages,
  WaterfallBundleConfigFormShape,
} from './WaterfallBundleConfigModal.utils';

export interface WaterfallBundleConfigModalProps {
  /** null for create mode, index for edit mode */
  bundleIndex: number | null;
  onClose: () => void;
}

/**
 * Reads the parent form's bundle data (for edit mode), then renders the modal
 * with its own nested FormProvider to avoid polluting the parent form.
 */
export function WaterfallBundleConfigModal({
  bundleIndex,
  onClose,
}: WaterfallBundleConfigModalProps) {
  const { control } = useFormContext<PresentationTemplateEditorFormShape>();

  // Read existing bundle data for edit mode before we shadow the parent form context
  const existingBundle = useWatch({
    control,
    name:
      bundleIndex !== null
        ? (`bundles.${bundleIndex}` as const)
        : // Watch a field that won't match to get undefined in create mode
          (undefined as never),
  }) as PresentationTemplateEditorBundle | undefined;

  const isEditMode = bundleIndex !== null;

  return (
    <WaterfallBundleConfigModalInner
      existingBundle={isEditMode ? existingBundle : undefined}
      bundleIndex={bundleIndex}
      onClose={onClose}
    />
  );
}

interface WaterfallBundleConfigModalInnerProps {
  existingBundle?: PresentationTemplateEditorBundle;
  bundleIndex: number | null;
  onClose: () => void;
}

function WaterfallBundleConfigModalInner({
  existingBundle,
  bundleIndex,
  onClose,
}: WaterfallBundleConfigModalInnerProps) {
  const { bundlesFormArray, markBundlesDirty } =
    usePresentationTemplateEditorContext();
  const isEditMode = bundleIndex !== null;
  const isUpdatedSummaryPageEnabled = useFeatureFlag(
    'waterfall_summary_page_update'
  );

  const form = useForm<WaterfallBundleConfigFormShape>({
    defaultValues: {
      pages: getDefaultWaterfallPages(
        existingBundle,
        isUpdatedSummaryPageEnabled
      ),
      templateNotes: existingBundle?.templateNotes ?? '',
    },
  });

  const watchedPages = useWatch({ control: form.control, name: 'pages' });

  const hasCheckedPages = useMemo(
    () => watchedPages?.some((p) => p.checked) ?? false,
    [watchedPages]
  );

  const handleSave = useCallback(() => {
    const formValues = form.getValues();
    const bundle = buildWaterfallBundleFromForm(
      formValues.pages,
      formValues.templateNotes,
      existingBundle
    );

    if (isEditMode && bundleIndex !== null) {
      bundlesFormArray.update(bundleIndex, bundle);
      // useFieldArray.update() doesn't propagate dirty state to the parent
      // form, so flip isDirty explicitly to keep the Save button enabled.
      markBundlesDirty();
    } else {
      bundlesFormArray.append(bundle);
    }

    onClose();
  }, [
    form,
    existingBundle,
    isEditMode,
    bundleIndex,
    bundlesFormArray,
    markBundlesDirty,
    onClose,
  ]);

  const actions = useMemo(
    () => (
      <Stack direction="row" justifyContent="flex-end" spacing={2} width="100%">
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={!hasCheckedPages}
        >
          {isEditMode ? 'Save' : 'Add selected bundle'}
        </Button>
      </Stack>
    ),
    [onClose, handleSave, hasCheckedPages, isEditMode]
  );

  return (
    <FormProvider {...form}>
      <FormModal
        heading={
          isEditMode ? 'Edit waterfall overview' : 'Add waterfall overview'
        }
        isOpen
        onClose={onClose}
        actions={actions}
        rightHeaderContent={null}
      >
        <Stack direction="column" spacing={2}>
          <FormAwareTextInput<WaterfallBundleConfigFormShape>
            fieldName={
              'templateNotes' as const satisfies PathsOf<WaterfallBundleConfigFormShape>
            }
            label="Presentation template notes"
            control={form.control}
            multiline
            rows={3}
          />

          <Stack direction="column" spacing={0.5}>
            <Typography variant="title-s">
              Select and reorder pages to include
            </Typography>
            {!isEditMode && (
              <Typography variant="body-xs">
                This can be changed after adding the bundle to your template.
              </Typography>
            )}
          </Stack>

          <WaterfallPageReorderList />
        </Stack>
      </FormModal>
    </FormProvider>
  );
}
